import {useNavigate, useSearchParams} from 'react-router-dom';
import {Key, useCallback, useEffect, useMemo, useState} from 'react';
import {BackendFilter} from './backend-filter';
import {BackendFiltersUrlKey} from './backend-filters-url-key';
import {decodeBackendFilters} from './utils/decode-backend-filters';
import {
  encodeBackendFilters,
  FilterListValue,
} from './utils/encode-backend-filters';

export function useBackendFilterUrlParams({
  filters = [],
  pinnedFilters = [],
  filterKeyPrefix = '',
}: {
  filters?: BackendFilter[];
  pinnedFilters?: string[];
  filterKeyPrefix?: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Construit une clé unique en fonction du préfixe si fourni
  const key = filterKeyPrefix
    ? `${filterKeyPrefix}-${BackendFiltersUrlKey}`
    : BackendFiltersUrlKey;
  const [encodedFilters, setEncodedFilters] = useState<string>(
    () => searchParams.get(key) || '',
  );

  useEffect(() => {
    // Met à jour l’état si les searchParams changent
    setEncodedFilters(searchParams.get(key) || '');
  }, [searchParams, key]);

  // Fonction pour mettre à jour les filtres dans l’URL
  const updateEncodedFilters = useCallback(
    (newFilters: string) => {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      if (newFilters) {
        newSearchParams.set(key, newFilters);
      } else {
        newSearchParams.delete(key);
      }
      setSearchParams(newSearchParams);
      setEncodedFilters(newFilters);
    },
    [key, searchParams, setSearchParams],
  );

  const decodedFilters: FilterListValue[] = useMemo(() => {
    if (!filters) return [];
    const decoded = decodeBackendFilters(encodedFilters);

    // Si un filtre est "pinned" et n'est pas encore appliqué, on ajoute un placeholder
    pinnedFilters.forEach(pinnedKey => {
      if (!decoded.find(f => f.key === pinnedKey)) {
        const config = filters.find(f => f.key === pinnedKey);
        if (config) {
          decoded.push({
            key: pinnedKey,
            value: config.control?.defaultValue,
            operator: config.defaultOperator,
            isInactive: true,
          });
        }
      }
    });

    // Conserver l'ordre des filtres tel que défini dans la configuration
    decoded.sort(
      (a, b) =>
        filters.findIndex(f => f.key === a.key) -
        filters.findIndex(f => f.key === b.key),
    );

    return decoded;
  }, [encodedFilters, pinnedFilters, filters]);

  const getDecodedWithoutKeys = useCallback(
    (values: (FilterListValue | Key)[]) => {
      const newFilters = [...decodedFilters];
      values.forEach(value => {
        const filterKey = typeof value === 'object' ? value.key : value;
        const index = newFilters.findIndex(f => f.key === filterKey);
        if (index > -1) {
          newFilters.splice(index, 1);
        }
      });
      return newFilters;
    },
    [decodedFilters],
  );

  const replaceAll = useCallback(
    (filterValues: FilterListValue[]) => {
      const newEncodedFilters = encodeBackendFilters(filterValues, filters);
      updateEncodedFilters(newEncodedFilters);
      // MàJ de l'URL avec navigate pour remplacer l'historique
      const newSearchParams = new URLSearchParams(searchParams.toString());
      if (newEncodedFilters) {
        newSearchParams.set(key, newEncodedFilters);
      } else {
        newSearchParams.delete(key);
      }
      navigate({search: `?${newSearchParams.toString()}`}, {replace: true});
    },
    [filters, key, navigate, searchParams, updateEncodedFilters],
  );

  const add = useCallback(
    (filterValues: FilterListValue[]) => {
      const existing = getDecodedWithoutKeys(filterValues);
      const newDecodedFilters = [...existing, ...filterValues];
      replaceAll(newDecodedFilters);
    },
    [getDecodedWithoutKeys, replaceAll],
  );

  const remove = useCallback(
    (filterKey: Key) => {
      const newDecodedFilters = getDecodedWithoutKeys([filterKey]);
      replaceAll(newDecodedFilters);
    },
    [getDecodedWithoutKeys, replaceAll],
  );

  return {
    add,
    remove,
    replaceAll,
    decodedFilters,
    encodedFilters,
    updateEncodedFilters,
  };
}
